import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from "react";
import { Interview, InterviewStatus, PaginationInfo } from "../../../types/interview";
import { interviewService } from "../../../services";
import { debugLog, debugError } from "../../../utils/debug";
import {
  SearchIcon,
  CloseIcon,
  CheckIcon,
  ClockIcon,
  PdfIcon,
  CopyIcon,
  ChevronUpDoubleIcon,
  Pill,
  StandardButton,
  Toast,
} from "../../ui";

const capitalizeFirstLetter = (value: string): string => {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const formatDate = (dateString: string): { date: string; time: string } => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return {
    date: `${day}/${month}/${year}`,
    time: `${hours}:${minutes}`,
  };
};

const getStatusPillProps = (status: InterviewStatus) => {
  switch (status) {
    case "pending":
      return {
        text: "Pending",
        color: "#4B5563",
        bgColor: "#F3F4F6",
        borderColor: "#E5E7EB",
      };
    case "inprogress":
      return {
        text: "In Progress",
        color: "#4B5563",
        bgColor: "#F3F4F6",
        borderColor: "#E5E7EB",
        icon: <ClockIcon color="#4B5563" />,
      };
    case "expired":
      return {
        text: "Expired",
        color: "#FFF",
        bgColor: "#63615C",
        borderColor: "#63615C",
      };
    case "completed":
      return {
        text: "Completed",
        color: "#24AEA8",
        bgColor: "#B1E4E3",
        borderColor: "rgba(153, 246, 228, 0.50)",
        icon: <CheckIcon />,
      };
    case "interrupted":
      return {
        text: "Ended",
        color: "#B45309",
        bgColor: "#FEF3C7",
        borderColor: "#FDE68A",
        icon: <CloseIcon color="#B45309" />,
      };
    default:
      return {
        text: status,
        color: "#4B5563",
        bgColor: "#F3F4F6",
        borderColor: "#E5E7EB",
      };
  }
};

const headerCellStyle: React.CSSProperties = {
  color: "#171C27",
  fontFamily: "Interstate",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "16px",
  textTransform: "uppercase",
  padding: "12px 0",
  textAlign: "left",
  whiteSpace: "nowrap",
};

const defaultCellStyle: React.CSSProperties = {
  color: "#111827",
  fontFamily: "Interstate",
  fontSize: "14.03px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "20px",
  padding: "16px 0",
  whiteSpace: "nowrap",
};

const truncatedCellStyle: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "block",
};

const emailCellStyle: React.CSSProperties = {
  color: "#6B7280",
  fontFamily: "Interstate",
  fontSize: "14.07px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "20px",
  padding: "16px 0",
  whiteSpace: "nowrap",
};

const datePrimaryStyle: React.CSSProperties = {
  color: "#111827",
  fontFamily: "Interstate",
  fontSize: "13.374px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "20px",
};

const dateSecondaryStyle: React.CSSProperties = {
  color: "#9CA3AF",
  fontFamily: "Interstate",
  fontSize: "11.584px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
};

const PAGE_SIZE = 10;

interface InterviewSessionListProps {
  onCreateNew?: () => void;
}

export interface InterviewSessionListHandle {
  refresh: () => void;
}

const DEBOUNCE_DELAY = 300;

const InterviewSessionList = forwardRef<InterviewSessionListHandle, InterviewSessionListProps>(({ onCreateNew }, ref) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [interviews, setInterviews] = useState<Interview[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    pageSize: PAGE_SIZE,
    totalPages: 0,
    totalCount: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageInput, setPageInput] = useState("1");
  const [toast, setToast] = useState<{ type: "success" | "failed"; message: string } | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialMount = useRef(true);

  const fetchInterviews = useCallback(async (page: number, search?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await interviewService.getAll({
        page,
        pageSize: PAGE_SIZE,
        search: search || undefined,
      });
      // Sort: pending first, then by date created descending
      setInterviews(response.data);
      setPagination(response.pagination);
      setPageInput(response.pagination.page.toString());
    } catch (err) {
      debugError("Failed to fetch interviews:", err);
      setError("Failed to fetch interviews. Please try again.");
      setInterviews([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useImperativeHandle(ref, () => ({
    refresh: () => {
      fetchInterviews(1, searchQuery.trim() || undefined);
    },
  }), [fetchInterviews, searchQuery]);

  useEffect(() => {
    fetchInterviews(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search effect
  useEffect(() => {
    // Skip debounce on initial mount (initial fetch is handled separately)
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      fetchInterviews(1, searchQuery.trim() || undefined);
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchQuery, fetchInterviews]);

  const handlePrevious = () => {
    if (pagination.page > 1) {
      fetchInterviews(pagination.page - 1, searchQuery.trim() || undefined);
    }
  };

  const handleNext = () => {
    if (pagination.page < pagination.totalPages) {
      fetchInterviews(pagination.page + 1, searchQuery.trim() || undefined);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleCopyLink = async (interview: Interview) => {
    await navigator.clipboard.writeText(interview.interviewUrl);
    setCopiedId(interview.id);
    setTimeout(() => setCopiedId(null), 2000);
    setToast({ type: "success", message: "Interview link copied successfully!" });
  };

  const handleDownload = async (interview: Interview) => {
    try {
      // Check all possible property names (camelCase, PascalCase)
      const interviewAny = interview as any;
      const reportUrl = interview.reportName ||
                        interviewAny.ReportName ||
                        interviewAny.reportUrl ||
                        interviewAny.ReportUrl;

      debugLog('Download clicked for interview:', interview.id);
      debugLog('Report URL (reportName):', interview.reportName);
      debugLog('Report URL (ReportName):', interviewAny.ReportName);
      debugLog('Report URL (reportUrl):', interviewAny.reportUrl);
      debugLog('Report URL (ReportUrl):', interviewAny.ReportUrl);
      debugLog('Final reportUrl value:', reportUrl);
      debugLog('Full interview object:', JSON.stringify(interview, null, 2));

      if (!reportUrl) {
        debugError('No report URL found in interview object');
        setToast({ type: "failed", message: "No report available for this interview." });
        return;
      }

      debugLog('Opening report URL:', reportUrl);
      // Open the report URL directly (reportName contains the full blob storage URL)
      window.open(reportUrl, '_blank');
      setToast({ type: "success", message: "Interview report opened successfully!" });
    } catch (err) {
      debugError("Failed to open report:", err);
      setToast({ type: "failed", message: "Failed to open interview report. Please try again." });
    }
  };

  const renderDateCell = (dateString?: string) => {
    if (!dateString) {
      return (
        <div style={{ ...defaultCellStyle, padding: 0 }}>-</div>
      );
    }
    const { date, time } = formatDate(dateString);
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <span style={datePrimaryStyle}>{date}</span>
        <span style={dateSecondaryStyle}>{time}</span>
      </div>
    );
  };

  const isSearching = searchQuery.trim().length > 0;
  const visibleCount = interviews.length;
  const totalItems = pagination.totalCount;

  return (
    <div
      style={{
        borderRadius: "8px",
        border: "1px solid #E5E7EB",
        background: "#FFF",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          borderBottom: "1px solid #E5E7EB",
          background: "rgba(0, 0, 0, 0.00)",
        }}
      >
        {/* Left side - Title and count */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            style={{
              color: "#63615C",
              fontFamily: "Interstate",
              fontSize: "17.598px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "28px",
            }}
          >
            {isSearching ? "Search Results" : "Interview Sessions"}
          </span>
          <span
            style={{
              borderRadius: "9999px",
              background: "#F3F4F6",
              color: "#4B5563",
              textAlign: "center",
              fontFamily: "Interstate",
              fontSize: "12px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "16px",
              padding: "2px 8px",
            }}
          >
            {totalItems}
          </span>
        </div>

        {/* Right side - Search input */}
        <div style={{ position: "relative", width: "275px" }}>
          <div
            style={{
              position: "absolute",
              left: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              alignItems: "center",
            }}
          >
            <SearchIcon />
          </div>
          <input
            type="text"
            placeholder="Search Job ID or Candidate..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="standard-input"
            style={{
              paddingLeft: "36px",
              paddingRight: searchQuery ? "36px" : "12px",
              width: "100%",
              height: "40px",
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              style={{
                position: "absolute",
                right: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                padding: 0,
              }}
            >
              <CloseIcon />
            </button>
          )}
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "60px",
            borderTop: "1px solid #E5E7EB",
          }}
        >
          <span
            style={{
              color: "#6B7280",
              fontFamily: "Interstate",
              fontSize: "14px",
            }}
          >
            Loading interviews...
          </span>
        </div>
      )}

      {/* Error State */}
      {!isLoading && error && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px",
            gap: "8px",
            borderTop: "1px solid #E5E7EB",
          }}
        >
          <span
            style={{
              color: "#DC2626",
              fontFamily: "Interstate",
              fontSize: "14px",
            }}
          >
            {error}
          </span>
          <StandardButton
            text="Retry"
            variant="outline"
            color="secondary"
            size="small"
            onClick={() => fetchInterviews(pagination.page, searchQuery.trim() || undefined)}
          />
        </div>
      )}

      {/* Table Container */}
      {!isLoading && !error && (
        <div
          style={{
            overflowX: "auto",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: "900px",
            }}
          >
            <thead>
              <tr style={{ background: "#F9FAFB" }}>
                <th style={{ ...headerCellStyle, paddingLeft: "24px" }}>Job ID</th>
                <th style={{ ...headerCellStyle, paddingLeft: "24px" }}>Candidate Name</th>
                <th style={{ ...headerCellStyle, paddingLeft: "24px" }}>Email</th>
                <th style={{ ...headerCellStyle, paddingLeft: "24px" }}>Date Generated</th>
                <th style={{ ...headerCellStyle, paddingLeft: "24px" }}>Date Completed</th>
                <th style={{ ...headerCellStyle, paddingLeft: "24px" }}>Status</th>
                <th style={{ ...headerCellStyle, paddingLeft: "24px", paddingRight: "24px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {interviews.map((interview) => {
                const statusProps = getStatusPillProps(interview.status);
                return (
                  <tr
                    key={interview.id}
                    style={{
                      borderTop: "1px solid #E5E7EB",
                      background: "rgba(0, 0, 0, 0.00)",
                      height: "56px",
                    }}
                  >
                    <td style={{ ...defaultCellStyle, paddingLeft: "24px", maxWidth: "120px" }}>
                      <span style={truncatedCellStyle} title={interview.jobId}>
                        {interview.jobId}
                      </span>
                    </td>
                    <td style={{ ...defaultCellStyle, paddingLeft: "24px", maxWidth: "180px" }}>
                      <span style={truncatedCellStyle} title={`${capitalizeFirstLetter(interview.firstName)} ${capitalizeFirstLetter(interview.lastName)}`}>
                        {capitalizeFirstLetter(interview.firstName)} {capitalizeFirstLetter(interview.lastName)}
                      </span>
                    </td>
                    <td style={{ ...emailCellStyle, paddingLeft: "24px", maxWidth: "200px" }}>
                      <span style={truncatedCellStyle} title={interview.email}>
                        {interview.email}
                      </span>
                    </td>
                    <td style={{ ...defaultCellStyle, paddingLeft: "24px" }}>
                      {renderDateCell(interview.createdDate)}
                    </td>
                    <td style={{ ...defaultCellStyle, paddingLeft: "24px" }}>
                      {renderDateCell(interview.completedDate)}
                    </td>
                    <td style={{ ...defaultCellStyle, paddingLeft: "24px" }}>
                      <Pill {...statusProps} />
                    </td>
                    <td style={{ ...defaultCellStyle, paddingLeft: "24px", paddingRight: "24px" }}>
                      {(interview.status === "completed" || interview.status === "interrupted") ? (
                        <StandardButton
                          text="Download"
                          variant="fill"
                          color="secondary"
                          size="small"
                          startIcon={<PdfIcon />}
                          onClick={() => handleDownload(interview)}
                        />
                      ) : (
                        <StandardButton
                          text={copiedId === interview.id ? "Copied!" : "Copy Link"}
                          variant="outline"
                          color="secondary"
                          size="small"
                          startIcon={copiedId === interview.id ? undefined : <CopyIcon />}
                          onClick={() => handleCopyLink(interview)}
                          disabled={interview.status === "expired"}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State - No Search Results */}
      {!isLoading && !error && isSearching && interviews.length === 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px",
            gap: "8px",
            borderTop: "1px solid #E5E7EB"
          }}
        >
          <span
            style={{
              color: "#63615C",
              textAlign: "center",
              fontFamily: "Interstate",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "normal",
              letterSpacing: "0.6px",
            }}
          >
            No candidates found!
          </span>
          <span
            style={{
              color: "#63615C",
              textAlign: "center",
              fontFamily: "Interstate",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "normal",
              letterSpacing: "0.6px",
            }}
          >
            Kindly refine your search keyword(s) and try again.
          </span>
        </div>
      )}

      {/* Empty State - No Interviews */}
      {!isLoading && !error && !isSearching && interviews.length === 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "60px",
            gap: "8px",
            borderTop: "1px solid #E5E7EB"
          }}
        >
          <span
            style={{
              color: "#63615C",
              textAlign: "center",
              fontFamily: "Interstate",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "normal",
              letterSpacing: "0.6px",
            }}
          >
            No Interview Sessions Yet!
          </span>
          <span
            style={{
              color: "#63615C",
              textAlign: "center",
              fontFamily: "Interstate",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "normal",
              letterSpacing: "0.6px",
            }}
          >
            Create your first interview session to get started.
          </span>
        </div>
      )}

      {/* Footer - Empty State with CTA Button */}
      {!isLoading && !error && !isSearching && interviews.length === 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "64px",
            borderTop: "1px solid #E5E7EB",
            background: "#FFF",
            borderRadius: "0 0 8px 8px",
          }}
        >
          <StandardButton
            text="Create New Interview Session"
            variant="text"
            color="secondary"
            onClick={onCreateNew}
            endIcon={
              <span className="bounce-up-down">
                <ChevronUpDoubleIcon color="#171C27" />
              </span>
            }
          />
        </div>
      )}

      {/* Footer - Pagination */}
      {!isLoading && !error && interviews.length > 0 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "12px 24px",
            borderTop: "1px solid #E5E7EB",
            background: "#FFF",
            borderRadius: "0 0 8px 8px",
          }}
        >
          {/* Left - Results count */}
          <span
            style={{
              color: "#6B7280",
              fontFamily: "Interstate",
              fontSize: "14px",
              fontWeight: 400,
            }}
          >
            Showing {visibleCount} of {totalItems} results
          </span>

          {/* Right - Pagination controls */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <button
              onClick={handlePrevious}
              disabled={pagination.page === 1}
              style={{
                borderRadius: "6px",
                border: pagination.page === 1 ? "none" : "1px solid #E5E7EB",
                background: pagination.page === 1 ? "transparent" : "#FFF",
                padding: "9px 17px",
                color: pagination.page === 1 ? "#63615C" : "#171C27",
                fontFamily: "Interstate",
                fontSize: "14px",
                fontWeight: 600,
                cursor: pagination.page === 1 ? "not-allowed" : "pointer",
              }}
            >
              Previous
            </button>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <input
                type="text"
                value={pageInput}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  setPageInput(value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const pageNum = parseInt(pageInput, 10);
                    if (pageNum >= 1 && pageNum <= pagination.totalPages) {
                      fetchInterviews(pageNum, searchQuery.trim() || undefined);
                    } else {
                      setPageInput(pagination.page.toString());
                    }
                  }
                }}
                onBlur={() => {
                  setPageInput(pagination.page.toString());
                }}
                style={{
                  width: "40px",
                  height: "32px",
                  textAlign: "center",
                  borderRadius: "6px",
                  border: "1px solid #E5E7EB",
                  background: "#FFF",
                  color: "#63615C",
                  fontFamily: "Interstate",
                  fontSize: "14px",
                  fontWeight: 500,
                  outline: "none",
                }}
              />
              <span
                style={{
                  color: "#63615C",
                  fontFamily: "Interstate",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                of {pagination.totalPages}
              </span>
            </div>
            <button
              onClick={handleNext}
              disabled={pagination.page === pagination.totalPages || pagination.totalPages === 0}
              style={{
                borderRadius: "6px",
                border: (pagination.page === pagination.totalPages || pagination.totalPages === 0) ? "none" : "1px solid #E5E7EB",
                background: (pagination.page === pagination.totalPages || pagination.totalPages === 0) ? "transparent" : "#FFF",
                padding: "9px 17px",
                color: (pagination.page === pagination.totalPages || pagination.totalPages === 0) ? "#63615C" : "#171C27",
                fontFamily: "Interstate",
                fontSize: "14px",
                fontWeight: 600,
                cursor: (pagination.page === pagination.totalPages || pagination.totalPages === 0) ? "not-allowed" : "pointer",
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
});

InterviewSessionList.displayName = "InterviewSessionList";

export default InterviewSessionList;
